/** Single palette: brand accents, opaque readable text/surfaces, neutral alpha layers, danger.
 * Tags share five semantic tones: neutral, info, warning, error and success, with readable theme pairs.
 * Media overlays and print colors remain opaque/fixed where background contrast requires it. */
const base = {
  ":root": {
    "--ui-fg": "238 238 238",
    "--ui-tag-neutral": "51 51 51",
    "--ui-tag-neutral-ink": "208 208 208",
    "--ui-tag-info": "35 60 88",
    "--ui-tag-info-ink": "175 212 255",
    "--ui-tag-warning": "74 50 31",
    "--ui-tag-warning-ink": "255 196 138",
    "--ui-tag-error": "74 40 40",
    "--ui-tag-error-ink": "255 180 174",
    "--ui-tag-success": "184 223 170",
    "--ui-tag-success-ink": "17 17 17",
    "--ui-neutral": "255 255 255",
    "--ui-text-secondary": "184 184 184",
    "--ui-text-muted": "156 156 156",
    "--ui-text-placeholder": "112 112 112",
    "--ui-text-inverse": "16 16 16",
    "--ui-text-inverse-secondary": "64 64 64",
    "--ui-surface": "27 27 27",
    "--ui-canvas": "16 16 16",
    "--ui-danger": "255 112 106",
    "--ui-warning": "255 166 77",
    "--ui-overlay": "0 0 0",
    "--ui-brand": "0 132 190",
    "--ui-brand-ink": "255 255 255",
    "--ui-brand-hover": "0 115 167",
    "--ui-brand-active": "0 102 150",
    "--ui-accent-text": "110 195 240",
    "--color-btn-primary": "rgb(var(--ui-brand))",
    "--primary-gradient": "linear-gradient(120deg, rgb(var(--ui-brand)), rgb(var(--ui-brand-active)))",
    "--ui-withpot-mark": "rgb(43 232 0)",
    "--ui-success-ink": "var(--ui-accent-text)",
    "--ui-paper": "255 255 255",
    "--ui-ink": "0 0 0",
    "--ui-danger-ink": "0 0 0",
    "--ui-brand-soft": "color-mix(in srgb, rgb(var(--ui-brand)) 10%, rgb(var(--ui-surface)))",
    "--ui-xiaohongshu": "255 36 66",
    "--ui-xiaohongshu-hover": "217 31 56"
  },
  ":root[data-theme=\"light\"]": {
    "--ui-fg": "0 0 0",
    "--ui-tag-neutral": "238 238 238",
    "--ui-tag-neutral-ink": "82 82 82",
    "--ui-tag-info": "226 237 253",
    "--ui-tag-info-ink": "24 92 164",
    "--ui-tag-warning": "255 235 213",
    "--ui-tag-warning-ink": "147 65 0",
    "--ui-tag-error": "255 228 225",
    "--ui-tag-error-ink": "179 38 32",
    "--ui-tag-success": "216 243 206",
    "--ui-tag-success-ink": "17 17 17",
    "--ui-neutral": "0 0 0",
    "--ui-text-secondary": "82 82 82",
    "--ui-text-muted": "102 102 102",
    "--ui-text-placeholder": "170 170 170",
    "--ui-text-inverse": "255 255 255",
    "--ui-text-inverse-secondary": "229 229 229",
    "--ui-surface": "255 255 255",
    "--ui-canvas": "247 247 247",
    "--ui-danger": "210 43 36",
    "--ui-warning": "183 83 0",
    "--ui-danger-ink": "255 255 255",
    "--ui-accent-text": "0 102 150",
    "--ui-withpot-mark": "rgb(var(--ui-ink))",
    "--ui-brand-soft": "color-mix(in srgb, rgb(var(--ui-brand)) 18%, rgb(var(--ui-paper)))"
  },
  ":root[data-brand='withpot']": {
    "--ui-brand": "43 232 0",
    "--ui-brand-hover": "39 213 0",
    "--ui-brand-active": "35 190 0",
    "--ui-brand-ink": "0 0 0",
    "--ui-accent-text": "var(--ui-fg)"
  }
};
const colors = {
  foreground: "rgb(var(--ui-fg) / <alpha-value>)",
  secondary: "rgb(var(--ui-text-secondary) / <alpha-value>)",
  muted: "rgb(var(--ui-text-muted) / <alpha-value>)",
  placeholder: "rgb(var(--ui-text-placeholder) / <alpha-value>)",
  inverse: "rgb(var(--ui-text-inverse) / <alpha-value>)",
  "inverse-secondary": "rgb(var(--ui-text-inverse-secondary) / <alpha-value>)",
  "ui-b": "rgb(var(--ui-fg) / <alpha-value>)",
  "ui-b01": "rgb(var(--ui-neutral) / .01)",
  "ui-b02": "rgb(var(--ui-neutral) / .02)",
  "ui-b05": "rgb(var(--ui-neutral) / .05)",
  "ui-b1": "rgb(var(--ui-neutral) / .1)",
  "ui-b2": "rgb(var(--ui-neutral) / .2)",
  "ui-b4": "rgb(var(--ui-neutral) / .4)",
  "ui-b6": "rgb(var(--ui-neutral) / .6)",
  "ui-w": "rgb(var(--ui-surface) / <alpha-value>)",
  "ui-surface-hover": "color-mix(in srgb, rgb(var(--ui-neutral)) 2%, rgb(var(--ui-surface)))",
  "ui-surface-selected": "color-mix(in srgb, rgb(var(--ui-neutral)) 10%, rgb(var(--ui-surface)))",
  "ui-w05": "rgb(var(--ui-surface) / .05)",
  "ui-w1": "rgb(var(--ui-surface) / .1)",
  "ui-w2": "rgb(var(--ui-surface) / .2)",
  "ui-bgb": "rgb(var(--ui-canvas) / <alpha-value>)",
  "ui-r": "rgb(var(--ui-danger) / <alpha-value>)",
  "ui-warning": "rgb(var(--ui-warning) / <alpha-value>)",
  "ui-g": "rgb(var(--ui-success-ink) / <alpha-value>)",
  "ui-r-05": "rgb(var(--ui-danger) / .05)",
  "ui-brand": "rgb(var(--ui-brand) / <alpha-value>)",
  "ui-brand-ink": "rgb(var(--ui-brand-ink) / <alpha-value>)",
  "ui-brand-hover": "rgb(var(--ui-brand-hover) / <alpha-value>)",
  "ui-brand-active": "rgb(var(--ui-brand-active) / <alpha-value>)",
  "ui-accent": "rgb(var(--ui-accent-text) / <alpha-value>)",
  "ui-overlay": "rgb(var(--ui-overlay) / <alpha-value>)",
  "ui-paper": "rgb(var(--ui-paper) / <alpha-value>)",
  "ui-ink": "rgb(var(--ui-ink) / <alpha-value>)",
  "ui-danger-ink": "rgb(var(--ui-danger-ink) / <alpha-value>)",
  "ui-xiaohongshu": "rgb(var(--ui-xiaohongshu) / <alpha-value>)",
  "ui-xiaohongshu-hover": "rgb(var(--ui-xiaohongshu-hover) / <alpha-value>)",
  "ui-brand-soft": "var(--ui-brand-soft)",
};
// Tag backgrounds and text are paired; success always uses dark neutral text.
for (const tone of ['neutral','info','warning','error','success']) {
  colors[`ui-tag-${tone}`] = `rgb(var(--ui-tag-${tone}) / <alpha-value>)`;
  colors[`ui-tag-${tone}-ink`] = `rgb(var(--ui-tag-${tone}-ink) / <alpha-value>)`;
}
const printColors = {
  "paper": "#ffffff",
  "ink": "#000000",
  "heading": "#111111",
  "body": "#333333",
  "muted": "#666666",
  "line": "#d6d6d6",
  "soft": "#f4f4f4",
  "subtle": "#fafafa",
  "warm": "#f7f6f2",
  "danger": "#c91919",
  "whiteOverlay": "rgba(255,255,255,.86)",
  "shadow": "rgba(0,0,0,.08)"
};
module.exports = { base, colors, printColors, canvas: { dark: `rgb(${base[":root"]["--ui-canvas"]})`, light: `rgb(${base[':root[data-theme="light"]']["--ui-canvas"]})` } };

// The catalogue resolves these same production tokens, not a second demonstration palette.
const colorPurposes = {
  foreground:'主要文字与图标', secondary:'正文辅助信息', muted:'次要标签与辅助信息', placeholder:'输入框未填写时的占位提示', inverse:'深色表面上的文字', 'inverse-secondary':'深色表面上的辅助文字',
  'ui-b':'主要文字与图标', 'ui-w':'卡片与输入表面', 'ui-bgb':'页面背景', 'ui-r':'危险操作与错误', 'ui-r-05':'危险操作悬停底色', 'ui-g':'正向状态文字',
  'ui-surface-hover':'实色表面悬停', 'ui-surface-selected':'实色表面选中',
  'ui-brand':'主要操作底色', 'ui-brand-ink':'主要操作文字与图标', 'ui-brand-hover':'主要操作悬停', 'ui-brand-active':'主要操作按下', 'ui-brand-soft':'品牌浅底', 'ui-accent':'强调文字',
  'ui-overlay':'遮罩与阴影', 'ui-paper':'固定白色介质', 'ui-ink':'固定黑色文字', 'ui-danger-ink':'危险色底上的文字', 'ui-xiaohongshu':'小红书入口', 'ui-xiaohongshu-hover':'小红书入口悬停',
};
for (const [tone,label] of Object.entries({neutral:'中性',info:'进行中／信息',warning:'待关注',error:'异常／超时',success:'完成／成功'})) {
  colorPurposes[`ui-tag-${tone}`] = `Tag ${label}底色`;
  colorPurposes[`ui-tag-${tone}-ink`] = `Tag ${label}文字`;
}
const printPurposes = {paper:'纸张',ink:'条码与纯黑文字',heading:'标题',body:'正文',muted:'辅助信息',line:'分隔线',soft:'浅底',subtle:'次浅底',warm:'暖色纸张',danger:'错误标记',whiteOverlay:'白色遮罩',shadow:'阴影'};
function colorChannels(token, mode, brand) {
  const values = {...base[':root'], ...(mode === 'light' ? base[':root[data-theme="light"]'] : {}), ...(brand === 'withpot' ? base[":root[data-brand='withpot']"] : {})};
  const resolve = value => value.replace(/var\((--[\w-]+)\)/g, (_, key) => resolve(values[key]));
  if (token === 'ui-brand-soft') {
    const foreground = resolve(values['--ui-brand']).split(' ').map(Number);
    const background = resolve(values[mode === 'light' ? '--ui-paper' : '--ui-surface']).split(' ').map(Number);
    const alpha = mode === 'light' ? .18 : .1;
    return {channels:foreground.map((channel,index)=>Math.round(channel*alpha+background[index]*(1-alpha))),alpha:1};
  }
  const expression = resolve(colors[token].replace('<alpha-value>','1'));
  const numbers = expression.match(/[\d.]+/g).map(Number);
  return {channels:numbers.slice(0,3),alpha:numbers[3] ?? 1};
}
function swatch(channels, alpha = 1) {
  const rgb = `RGB ${channels.join(', ')}`;
  const hex = '#' + channels.map(channel=>channel.toString(16).padStart(2,'0')).join('').toUpperCase();
  return {rgb,hex,alpha,background:`rgba(${channels.join(',')},${alpha})`};
}
function colorEntry(token, brand = 'withpot') {
  const variants = ['light','dark'].map(mode=>{
    const {channels,alpha}=colorChannels(token,mode,brand);
    return {label:mode==='light'?'浅色':'深色',...swatch(channels,alpha)};
  });
  return {name:token, purpose:colorPurposes[token] ?? `交互叠加层：${token.startsWith('ui-b')?'黑／白':'表面'} ${Math.round(variants[0].alpha*100)}%`, variants};
}
const brandTokens = ['ui-brand','ui-brand-ink','ui-brand-hover','ui-brand-active','ui-brand-soft','ui-accent'];
const groups = [
  {title:'品牌 · WITHPOT ERP',tokens:brandTokens,brand:'withpot'},
  {title:'品牌 · 海海快递',tokens:brandTokens,brand:'logistics'},
  {title:'文字',tokens:['foreground','secondary','muted','placeholder','inverse','inverse-secondary']},
  {title:'背景与介质',tokens:['ui-w','ui-bgb','ui-paper','ui-ink']},
  {title:'错误与危险',tokens:['ui-r','ui-r-05','ui-danger-ink','ui-warning']},
  {title:'正向反馈',tokens:['ui-g']},
  {title:'状态标签',tokens:Object.keys(colors).filter(token=>token.startsWith('ui-tag-'))},
  {title:'外部平台标识',tokens:['ui-xiaohongshu','ui-xiaohongshu-hover']},
  {title:'交互叠加与边界',tokens:Object.keys(colors).filter(token=>/^ui-[bw]\d+$/.test(token)).concat('ui-overlay')},
];
const colorCatalog = groups.map(group=>({title:group.title,items:group.tokens.map(token=>colorEntry(token,group.brand))}));
// foreground and ui-b are the same value, one visible sample with both real aliases.
colorCatalog.find(group=>group.title==='文字').items[0].name = 'foreground / ui-b';
colorCatalog.push({title:'分享与打印',items:Object.entries(printColors).map(([name,value])=>{
  const numbers=value.startsWith('#') ? [1,3,5].map(index=>parseInt(value.slice(index,index+2),16)) : value.match(/[\d.]+/g).map(Number);
  return {name:`printColors.${name}`,purpose:printPurposes[name],variants:[{label:'固定色',...swatch(numbers.slice(0,3),numbers[3] ?? 1)}]};
})});
module.exports.colorCatalog = colorCatalog;
